/**
 * Pure helpers that derive an MCP `SelectionSnapshot` from a selected
 * tree-node id + the surrounding UI store fields. Extracted from
 * `useMcpContextSync.ts` so the parsing logic is unit-testable.
 *
 * The MCP selection-context surface is what AI agents read to know "what is
 * the user pointing at right now" — wiping it on a pinned-row click (because
 * the ID parser didn't recognise `pinned-*` types) was bug F6 in the
 * pinning-feature code review.
 */

import { parseNodeId } from '../../explorer/utils/nodeIdUtils';
import type { SelectionSnapshot, ExplorerObjectType } from '../../../types/mcpAppContext';

/**
 * Map a node `type` literal to the high-level MCP object-type taxonomy.
 * `null` means "no MCP-visible kind" — the parse-snapshot continues anyway
 * if the rest of the id is well-formed.
 */
export function mapNodeTypeToExplorerObjectType(nodeType: string): ExplorerObjectType | null {
  const mapping: Record<string, ExplorerObjectType> = {
    connection: 'connection',
    database: 'database',
    'tables-folder': 'folder',
    'views-folder': 'folder',
    'programmability-folder': 'folder',
    'stored-procedures-folder': 'folder',
    'functions-folder': 'folder',
    'system-databases-folder': 'folder',
    'columns-folder': 'folder',
    'keys-folder': 'folder',
    'constraints-folder': 'folder',
    'triggers-folder': 'folder',
    'indexes-folder': 'folder',
    'statistics-folder': 'folder',
    column: 'column',
    object: 'table', // refined by `refineObjectType` for 'object' nodes
    // Pinned wrapper rows surface the same MCP taxonomy as the underlying
    // type so AI agents see "user pointed at a database / folder / object"
    // regardless of whether they reached it via the pinned shortcut or the
    // regular Databases drill-down.
    'pinned-database': 'database',
    'pinned-schema': 'folder',
    'pinned-folder': 'folder',
    'pinned-object': 'table', // refined by `refineObjectType` below
  };
  return mapping[nodeType] ?? null;
}

/**
 * For `object<->...` / `pinned-object<->...` ids, the in-id third segment
 * carries the underlying object-type literal — refine accordingly.
 */
function refineObjectType(parts: string[]): ExplorerObjectType {
  if (parts.length >= 4) {
    const potentialType = parts[3].toLowerCase();
    if (potentialType.includes('view')) return 'view';
    if (potentialType.includes('proc') || potentialType.includes('stored')) return 'procedure';
    if (potentialType.includes('func')) return 'function';
    if (potentialType.includes('trigger')) return 'trigger';
    if (potentialType.includes('index')) return 'index';
    if (potentialType.includes('type')) return 'type';
  }
  return 'table';
}

/**
 * Map the camelCase on-disk folder enum (used in `PinnedObjectRef`) to the
 * kebab-case form produced by `nodeType.replace('-folder', '')` for canonical
 * folder rows. Keeps MCP `objectName` consistent across both selection paths.
 */
const FOLDER_KIND_KEBAB: Record<string, string> = {
  tables: 'tables',
  views: 'views',
  storedProcedures: 'stored-procedures',
  functions: 'functions',
  programmability: 'programmability',
};

function folderKindToKebab(folderKind: string): string {
  return FOLDER_KIND_KEBAB[folderKind] ?? folderKind;
}

function emptySnapshot(): SelectionSnapshot {
  return {
    hasSelection: false,
    objectType: null,
    objectName: null,
    schema: null,
    database: null,
    connectionId: null,
    path: null,
    capturedAt: new Date().toISOString(),
  };
}

/**
 * Marker segment of a workspace-pin tree-node id
 * (`${connectionNodeId}:pinned-workspace:${pinId}` — see
 * `createPinnedWorkspaceNode`). These ids are NOT part of the `<->` scheme:
 * `parseNodeId` splits one into `['connection', '{cid}:pinned-workspace:{id}']`,
 * which would hit the `case 'connection'` arm below and fabricate a snapshot
 * with objectType 'connection' and the mangled tail as objectName/connectionId.
 */
const WORKSPACE_PIN_ID_MARKER = ':pinned-workspace:';

export function createSelectionSnapshot(
  selectedNodeId: string | undefined,
  selectedConnectionId: string | undefined,
  selectedDatabase: string | undefined,
): SelectionSnapshot {
  if (!selectedNodeId) return emptySnapshot();

  // Workspace-pin rows (Task 15) — a content-bearing pin (saved table view /
  // query / notebook) is not a schema object and `ExplorerObjectType` has no
  // honest kind for it, so we emit a connection-scoped selection carrying the
  // pin's envelope database (threaded via `selectedDatabase` by the explorer's
  // click handler) and NO objectType/objectName fabrication. `hasSelection`
  // stays true — the user genuinely is pointing at something; MCP consumers
  // that need object identity see "no object", not invented values.
  const wsMarkerIdx = selectedNodeId.indexOf(WORKSPACE_PIN_ID_MARKER);
  if (wsMarkerIdx >= 0) {
    // The id prefix before the marker is the parent connection node's
    // canonical `connection<->{cid}` id — parse it for the clean fallback
    // connection id (parts[1] of the FULL id would be the mangled tail).
    const parentParts = parseNodeId(selectedNodeId.slice(0, wsMarkerIdx));
    const parsedConnectionId =
      parentParts[0] === 'connection' && parentParts.length >= 2 ? parentParts[1] : undefined;
    const connectionId = selectedConnectionId ?? parsedConnectionId ?? null;
    const database = selectedDatabase ?? null;
    const wsPathParts = [connectionId, database].filter((p): p is string => !!p);
    return {
      hasSelection: true,
      objectType: null,
      objectName: null,
      schema: null,
      database,
      connectionId,
      path: wsPathParts.length > 0 ? wsPathParts.join('/') : null,
      capturedAt: new Date().toISOString(),
    };
  }

  const parts = parseNodeId(selectedNodeId);
  if (parts.length < 2) return emptySnapshot();

  const nodeType = parts[0];
  const connectionId = parts[1];
  let objectType = mapNodeTypeToExplorerObjectType(nodeType);
  let objectName: string | null = null;
  let schema: string | null = null;
  let database: string | null = selectedDatabase ?? null;

  switch (nodeType) {
    case 'connection':
      objectName = connectionId;
      break;

    case 'database':
      if (parts.length >= 3) {
        objectName = parts[2];
        database = parts[2];
      }
      break;

    case 'column':
      if (parts.length >= 6) {
        database = parts[2];
        schema = parts[3];
        objectName = parts[5];
      }
      break;

    case 'object':
      objectType = refineObjectType(parts);
      if (parts.length >= 6) {
        database = parts[2];
        schema = parts[4];
        objectName = parts[5];
      } else if (parts.length >= 5) {
        database = parts[2];
        objectName = parts[4];
      } else if (parts.length >= 4) {
        schema = parts[2];
        objectName = parts[3];
      }
      break;

    // Pinned wrapper rows — same id shape as the underlying nodes, with the
    // type literal prefixed by `pinned-`. Without these branches a click on a
    // pinned row landed in the default `endsWith('-folder')` arm (which only
    // pinned-folder matches), leaving `database`/`objectName` empty and the
    // MCP selection blank.
    case 'pinned-database':
      // Format: pinned-database<->cid<->database
      if (parts.length >= 3) {
        objectName = parts[2];
        database = parts[2];
      }
      break;

    case 'pinned-schema':
      // Format: pinned-schema<->cid<->database<->schema
      if (parts.length >= 4) {
        database = parts[2];
        schema = parts[3];
        objectName = parts[3];
      }
      break;

    case 'pinned-folder':
      // Format: pinned-folder<->cid<->database<->folderKind
      // Review finding #11 — emit the kebab-case form so it matches the regular
      // folder branch below (`nodeType.replace('-folder', '')` produces kebab).
      // The on-disk PinnedObjectRef folder enum is camelCase (`storedProcedures`)
      // but MCP consumers see two different objectName strings for the same
      // logical click target unless we normalize here.
      if (parts.length >= 4) {
        database = parts[2];
        objectName = folderKindToKebab(parts[3]);
      }
      break;

    case 'pinned-object':
      // Format: pinned-object<->cid<->database<->objectType<->schema<->name
      objectType = refineObjectType(parts);
      if (parts.length >= 6) {
        database = parts[2];
        schema = parts[4];
        objectName = parts[5];
      }
      break;

    default:
      // Folders: tables-folder<->connectionId<->database
      if (nodeType.endsWith('-folder') && parts.length >= 3) {
        database = parts[2];
        objectName = nodeType.replace('-folder', '');
      }
      break;
  }

  const pathParts: string[] = [];
  if (connectionId) pathParts.push(connectionId);
  if (database) pathParts.push(database);
  if (schema) pathParts.push(schema);
  if (objectName && objectType !== 'connection' && objectType !== 'database') {
    pathParts.push(objectName);
  }
  const path = pathParts.length > 0 ? pathParts.join('/') : null;

  return {
    hasSelection: true,
    objectType,
    objectName,
    schema,
    database,
    connectionId: selectedConnectionId ?? connectionId,
    path,
    capturedAt: new Date().toISOString(),
  };
}
